'use client';

import { Content } from '@prismicio/client';
import { screenSizes } from '@/constants';
import { useScreenSize } from '@/hooks/useScreenSize';
import { Swiper, SwiperClass, SwiperSlide } from 'swiper/react';
import { useRef, useState, useMemo } from 'react';
import SlideCard from './SlideCard/SlideCard';
import { Button } from '@/components';
import { BlueCheckmarkIcon } from '@/assets/icons/PartnerPagesIcons';

import 'swiper/css';
import './swiper.scss';
import styles from './Slider.module.scss';

const REFERENCE_WIDTH = 1440;
const REFERENCE_SLIDES_PER_VIEW = 2.25817;

const getSlidesPerView = (width: number) => {
  if (width < screenSizes.middleDesktop) return 1;
  if (width >= REFERENCE_WIDTH) return REFERENCE_SLIDES_PER_VIEW;
  return (width / REFERENCE_WIDTH) * REFERENCE_SLIDES_PER_VIEW;
};

interface Props {
  slice: Content.ExtendedTestimonialsSlice;
}

const Slider = ({ slice }: Props) => {
  const { width } = useScreenSize();
  const swiperRef = useRef<SwiperClass | null>(null);
  const [activeIndex, setActiveIndex] = useState(1);

  const breakpoints = useMemo(
    () => ({
      [screenSizes.mobile]: {
        slidesPerView: 1,
      },
      [screenSizes.tablet]: {
        slidesPerView: 1,
      },
      [screenSizes.middleDesktop]: {
        slidesPerView: getSlidesPerView(width),
      },
    }),
    [width]
  );

  const goToSlide = (index: number) => {
    if (swiperRef.current) {
      swiperRef.current.slideTo(index);
    }
  };

  const checkIfBoundarySlide = (swiperInstance: SwiperClass) => {
    setActiveIndex(swiperInstance.activeIndex);
  };

  return (
    <div className={styles.wrapper}>
      <Swiper
        spaceBetween={16}
        centeredSlides
        className={styles.swiper}
        onSwiper={(swiperInstance) => {
          swiperRef.current = swiperInstance;
        }}
        onSlideChange={(swiperInstance) => {
          checkIfBoundarySlide(swiperInstance);
        }}
        breakpoints={breakpoints}
        initialSlide={1}
      >
        {slice.primary.testimonials.map((item, index) => (
          <SwiperSlide key={`${index} ${item.text}`} className={styles.slide}>
            <SlideCard card={item} />
          </SwiperSlide>
        ))}
      </Swiper>
      <div
        className={styles.dots}
        role="tablist"
        aria-label="Testimonial slides"
      >
        {slice.primary.testimonials.map((_, index) => (
          <button
            key={index}
            type="button"
            role="tab"
            aria-selected={activeIndex === index}
            aria-label={`Go to slide ${index + 1}`}
            className={`${styles.dot} ${activeIndex === index ? styles.dotActive : ''}`}
            onClick={() => goToSlide(index)}
          />
        ))}
      </div>
      <div className={styles.buttonWrapper}>
        <Button
          text={slice.primary.button_text}
          link={slice.primary.button_link}
          styles={`${styles.button} ${styles.desktopButton}`}
          isArrow
          variation="white"
        />
        <Button
          text={slice.primary.mobile_button_text}
          link={slice.primary.button_link}
          styles={`${styles.button} ${styles.mobileButton}`}
          isArrow
          variation="white"
        />
        <div className={styles.label}>
          <div className={styles.checkmark}>
            <BlueCheckmarkIcon />
          </div>
          {slice.primary.label}
        </div>
      </div>
    </div>
  );
};

export default Slider;
